import { useCallback, useEffect, useMemo, useRef } from "react";

import { registerDismissable } from "./dismissableRegistry";

export interface UsePopoverOptions {
  open: boolean;
  /** Called when the platform closes the popover behind React's back (light dismiss, Escape). */
  onOpenChange: (open: boolean) => void;
  /** "manual" gets no light dismiss and is not counted in the dismissable registry. */
  mode?: "auto" | "manual";
}

export interface UsePopoverResult {
  popoverRef: React.RefObject<HTMLDivElement | null>;
  popoverProps: {
    popover: "auto" | "manual";
    onToggle: React.ToggleEventHandler<HTMLDivElement>;
  };
}

/** Drives a Popover API surface from React state; spread `popoverProps` and attach `popoverRef`. */
export function usePopover({
  open,
  onOpenChange,
  mode = "auto",
}: UsePopoverOptions): UsePopoverResult {
  const popoverRef = useRef<HTMLDivElement>(null);

  const openRef = useRef(open);
  const onOpenChangeRef = useRef(onOpenChange);
  useEffect(() => {
    openRef.current = open;
    onOpenChangeRef.current = onOpenChange;
  });

  /* Only "auto" consumes Escape; counting a manual popover (e.g. a hover
     tooltip) would stop Companion's Esc-Esc reaching switch-user. A manual
     host that does consume Escape calls `registerDismissable` itself. */
  useEffect(() => {
    if (!open || mode !== "auto") return undefined;
    return registerDismissable();
  }, [open, mode]);

  useEffect(() => {
    const element = popoverRef.current;
    if (!element) return;
    try {
      const isShown = element.matches(":popover-open");
      if (open && !isShown) element.showPopover();
      else if (!open && isShown) element.hidePopover();
    } catch {
      // Transiently throws while disconnected or before the popover attribute lands; the next commit settles it.
    }
  }, [open]);

  const handleToggle = useCallback(
    (event: React.ToggleEvent<HTMLDivElement>) => {
      // React re-dispatches nested toggle events through ancestor popovers.
      if (event.target !== event.currentTarget) return;
      if (event.newState === "closed" && openRef.current)
        onOpenChangeRef.current(false);
    },
    [],
  );

  const popoverProps = useMemo(
    () => ({ popover: mode, onToggle: handleToggle }),
    [mode, handleToggle],
  );

  return { popoverRef, popoverProps };
}
